import { Facebook, Instagram } from "lucide-react";
import { SOCIAL_LINKS, withUtm } from "../lib/social";

/**
 * Brand-marked icon for X (Twitter) , Lucide doesn't ship the current
 * monogram, so we inline the official mark as an SVG path.
 */
const XIcon = ({ size = 16, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    aria-hidden="true"
    fill="currentColor"
    className={className}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817-5.97 6.817H1.674l7.73-8.836L1.254 2.25h6.828l4.713 6.231 5.45-6.231Zm-1.16 17.52h1.833L7.084 4.126H5.117l11.967 15.643Z" />
  </svg>
);

/**
 * TikTok mark, same treatment , inlined as an SVG path so it stays
 * sharp at any pixel ratio and inherits currentColor.
 */
const TikTokIcon = ({ size = 16, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    aria-hidden="true"
    fill="currentColor"
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.94a8.16 8.16 0 0 0 4.77 1.52V7.05a4.85 4.85 0 0 1-1.84-.36Z" />
  </svg>
);

const ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  tiktok: TikTokIcon,
  x: XIcon,
};

/**
 * A reusable row of MediTalk's official social links. Renders the same
 * four icons everywhere (consistency = trust). Supports two visual
 * variants so it can live on dark or light surfaces, and tags every
 * outbound link with UTMs so PostHog can attribute follower clicks
 * back to the on-site placement.
 *
 * Always opens links in a new tab with `noopener noreferrer` so we
 * don't lose the back-button journey or leak referrer state.
 */
export default function SocialLinksRow({
  source = "site",
  medium = "footer",
  campaign = "follow",
  variant = "dark", // "dark" | "light"
  size = "md", // "sm" | "md" | "lg"
  className = "",
  testIdPrefix = "social-link",
}) {
  const tileSizeClass = {
    sm: "h-8 w-8",
    md: "h-9 w-9",
    lg: "h-11 w-11",
  }[size];
  const iconPx = { sm: 14, md: 15, lg: 18 }[size];

  const tileBase =
    variant === "dark"
      ? "bg-white/[0.04] border-white/10 text-white/70 hover:text-white hover:border-[var(--lime)]/40 hover:bg-[var(--lime)]/10"
      : "bg-white border-[var(--light-border)] text-[var(--charcoal)] hover:text-[var(--lime-dark)] hover:border-[var(--lime-dark)]/40 hover:bg-[var(--lime)]/10";

  return (
    <ul
      className={`flex items-center gap-2 ${className}`}
      aria-label="Follow MediTalk on social media"
      data-testid="social-links-row"
    >
      {SOCIAL_LINKS.map((s) => {
        const Icon = ICONS[s.id];
        return (
          <li key={s.id}>
            <a
              href={withUtm(s.href, { source, medium, campaign })}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`MediTalk on ${s.label} (opens in new tab)`}
              title={`MediTalk on ${s.label}`}
              className={`inline-flex items-center justify-center rounded-lg border transition-colors ${tileSizeClass} ${tileBase}`}
              data-testid={`${testIdPrefix}-${s.id}`}
            >
              <Icon size={iconPx} className="" />
            </a>
          </li>
        );
      })}
    </ul>
  );
}
